import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backend';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  Phone, Mail, MessageCircle, Instagram, MapPin, Music2, Users, User,
  Sparkles, Clock, Check, ChevronDown, ChevronRight, CalendarDays, Star, ArrowRight,
  Volume2, VolumeX,
} from 'lucide-react';

const IMG = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/3466f891-9364-4da2-b820-b9fab48b4f44/images/9aa769e08bebc027a4332afa96caf5cc623b6d20df07a9d9eb288ed0fcc1aeb7.jpeg',
  group: 'https://static.prod-images.emergentagent.com/jobs/3466f891-9364-4da2-b820-b9fab48b4f44/images/ebd33be44eb1c11523222e5fa3327f49907c3decce5dc132bcecbfc46bdf80fd.jpeg',
  keys: 'https://static.prod-images.emergentagent.com/jobs/3466f891-9364-4da2-b820-b9fab48b4f44/images/281d868248970b53176a9244c991b008a1a5397b33fa38c84af9f8cf2fde9895.jpeg',
  individual: 'https://static.prod-images.emergentagent.com/jobs/3466f891-9364-4da2-b820-b9fab48b4f44/images/c83382536f8212f01180ebde2d032fd4d38ddf5821a88968e67b887b5532dd5d.jpeg',
  teacher: 'https://static.prod-images.emergentagent.com/jobs/3466f891-9364-4da2-b820-b9fab48b4f44/images/419e2332ee127bc2e84773bf9291a06b84bfee3128c3e59b5a0ec2ebc8711ca0.jpeg',
  logo: 'https://customer-assets-4nw71qhi.emergentagent.net/job_3466f891-9364-4da2-b820-b9fab48b4f44/artifacts/b8iysmyu_logo_1.jpeg',
  aula1: 'https://static.prod-images.emergentagent.com/jobs/3466f891-9364-4da2-b820-b9fab48b4f44/images/051c507d977c72ff7dee4343d391fa413274cd83be71f53021ee1bb72047d4d0.jpeg',
  aula2: 'https://static.prod-images.emergentagent.com/jobs/3466f891-9364-4da2-b820-b9fab48b4f44/images/c85182df09c2b79ad21919390dc42279e8d2f1d73dead6f6ca2df21163b9551a.jpeg',
};

// Contatti — WhatsApp/telefono placeholder cliccabili (aggiornabili dopo)
const WHATSAPP = 'https://wa.me/393447341134';
const PHONE = 'tel:+393447341134';
const EMAIL = 'info@mesmusicandenglish.it';
const INSTAGRAM = 'https://instagram.com/mesmusicandenglish';

// Hero: video fly-in nell'aula + poster (ultimo frame del video stesso)
const HERO_VIDEO = 'https://customer-assets-4nw71qhi.emergentagent.net/job_3466f891-9364-4da2-b820-b9fab48b4f44/artifacts/uiueclan_flying-scene-into-class.mp4';
const HERO_POSTER = '/mes-hero-poster.jpg';

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// Lightweight scroll-reveal
const useReveal = () => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setShown(true); obs.disconnect(); } },
      { threshold: 0.15 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return [ref, shown];
};

const Reveal = ({ children, delay = 0, className = '' }) => {
  const [ref, shown] = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : 'translateY(28px)',
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const PrimaryCTA = ({ onClick, children, className = '', testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`group inline-flex items-center justify-center gap-2 rounded-full bg-[#C9A24B] px-7 py-3.5 text-sm font-semibold uppercase tracking-wider text-[#14213D] shadow-[0_10px_30px_-8px_rgba(201,162,75,0.7)] transition-all duration-300 hover:bg-[#D8B968] hover:-translate-y-0.5 hover:shadow-[0_16px_36px_-8px_rgba(201,162,75,0.8)] ${className}`}
  >
    {children}
    <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
  </button>
);

const SecondaryCTA = ({ onClick, children, className = '', testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center justify-center gap-2 rounded-full border border-[#14746F]/40 bg-[#0E4C4C]/5 px-7 py-3.5 text-sm font-semibold uppercase tracking-wider text-[#0E4C4C] transition-all duration-300 hover:bg-[#0E4C4C] hover:text-[#F7F1E6] ${className}`}
  >
    <CalendarDays size={16} /> {children}
  </button>
);

const Kicker = ({ children }) => (
  <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.32em] text-[#14746F]">{children}</p>
);

const FAQ_ITEMS = [
  { q: 'Qual è l’età giusta per iniziare?', a: 'Dai 4 anni con la propedeutica in piccolo gruppo, dai 6 anni con le lezioni individuali. Ogni percorso è pensato per la fase di sviluppo del bambino.' },
  { q: 'Serve esperienza musicale precedente?', a: 'No. I nostri percorsi partono dall’inizio: il primo approccio al pianoforte avviene in modo graduale, musicale e sereno.' },
  { q: 'Serve avere un pianoforte a casa?', a: 'Non è necessario per iniziare. Con il tempo consigliamo uno strumento per esercitarsi, ma i primi passi si fanno insieme in aula.' },
  { q: 'Che differenza c’è tra gruppo e individuale?', a: 'Il gruppo (4-6 anni, 2-4 bambini) sviluppa ritmo, ascolto e gioco musicale in modo condiviso. L’individuale (dai 6 anni) permette una personalizzazione completa del percorso.' },
  { q: 'Come prenoto la prima lezione gratuita?', a: 'Compila il form in fondo alla pagina o scrivici su WhatsApp: ti ricontattiamo con le prime disponibilità, senza impegno.' },
];

const MesPianoLanding = () => {
  usePageMeta({
    title: 'Corsi di pianoforte per bambini a Legnano — MES Music and English School',
    canonicalPath: '/mes-pianoforte',
  });

  const [form, setForm] = useState({
    parent_name: '', child_name: '', child_age: '', phone: '', email: '', interest: '', message: '',
  });
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [openFaq, setOpenFaq] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const videoRef = useRef(null);

  const toggleSound = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (soundOn) {
      v.muted = true;
      setSoundOn(false);
    } else {
      v.muted = false;
      if (v.ended || v.currentTime >= v.duration - 0.1) v.currentTime = 0;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
      setSoundOn(true);
    }
  }, [soundOn]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.parent_name.trim() || !form.email.trim()) return;
    setStatus('sending');
    try {
      await axios.post(`${BACKEND_URL}/api/mes/lead`, { ...form, source: 'mes_landing' });
      setStatus('done');
      setForm({ parent_name: '', child_name: '', child_age: '', phone: '', email: '', interest: '', message: '' });
    } catch {
      setStatus('error');
    }
  }, [form]);

  const goForm = () => scrollTo('contatti');
  const goOpenDay = () => scrollTo('open-day');

  return (
    <div className="min-h-screen bg-[#F7F1E6] font-sans text-[#14213D] antialiased" data-testid="mes-landing">
      {/* Header */}
      <header className="fixed inset-x-0 top-0 z-40 border-b border-[#14213D]/5 bg-[#F7F1E6]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <div className="flex items-center gap-3">
            <img src={IMG.logo} alt="MES Music and English School" className="h-10 w-10 rounded-full object-cover ring-1 ring-[#C9A24B]/40" />
            <span className="hidden text-sm font-semibold tracking-wide sm:block">MES <span className="text-[#14746F]">Music&nbsp;and&nbsp;English&nbsp;School</span></span>
          </div>
          <nav className="hidden items-center gap-7 text-sm font-medium text-[#14213D]/70 lg:flex">
            <button onClick={() => scrollTo('percorsi')} className="transition-colors hover:text-[#14746F]">Percorsi</button>
            <button onClick={() => scrollTo('metodo')} className="transition-colors hover:text-[#14746F]">Il metodo</button>
            <button onClick={() => scrollTo('aula')} className="transition-colors hover:text-[#14746F]">L’aula</button>
            <button onClick={() => scrollTo('prezzi')} className="transition-colors hover:text-[#14746F]">Prezzi</button>
            <button onClick={() => scrollTo('open-day')} className="transition-colors hover:text-[#14746F]">Open Day</button>
            <button onClick={() => scrollTo('faq')} className="transition-colors hover:text-[#14746F]">FAQ</button>
          </nav>
          <PrimaryCTA onClick={goForm} testid="mes-header-cta" className="!px-5 !py-2.5 !text-xs">Prenota la lezione gratuita</PrimaryCTA>
        </div>
      </header>

      {/* Hero */}
      <section className="relative flex min-h-[92vh] items-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          {reduceMotion ? (
            <img src={HERO_POSTER} alt="Lezione di pianoforte per bambini nell'aula MES a Legnano" className="h-full w-full object-cover" data-testid="mes-hero-poster" />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              preload="auto"
              poster={HERO_POSTER}
              className="h-full w-full object-cover"
              data-testid="mes-hero-video"
            >
              <source src={HERO_VIDEO} type="video/mp4" />
            </video>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-[#14213D]/92 via-[#14213D]/70 to-[#14213D]/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#14213D]/60 to-transparent" />
          {!reduceMotion && (
            <button
              onClick={toggleSound}
              data-testid="mes-hero-sound-toggle"
              aria-label={soundOn ? 'Disattiva audio' : 'Attiva audio'}
              className="absolute bottom-6 right-6 z-10 inline-flex items-center gap-2 rounded-full border border-[#F7F1E6]/25 bg-[#14213D]/40 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#F7F1E6] backdrop-blur-md transition-all duration-300 hover:border-[#E9D9AE] hover:text-[#E9D9AE]"
            >
              {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
              <span className="hidden sm:inline">{soundOn ? 'Audio attivo' : 'Ascolta l’aula'}</span>
            </button>
          )}
        </div>
        <div className="relative mx-auto w-full max-w-6xl px-5">
          <div className="max-w-2xl">
            <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#C9A24B]/40 bg-[#14213D]/30 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#E9D9AE] backdrop-blur-sm">
              <Music2 size={13} /> Nuovi corsi · da ottobre 2026 · Legnano
            </p>
            <h1 className="font-serif text-4xl font-bold leading-[1.08] text-[#F7F1E6] sm:text-5xl lg:text-6xl" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
              Il primo pianoforte,<br />
              <span className="text-[#E9D9AE]">con metodo e meraviglia</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#F7F1E6]/85 sm:text-lg">
              A Legnano, i nuovi corsi di pianoforte per bambini firmati MES Music and English School:
              piccoli gruppi per i 4-6 anni, lezioni individuali dai 6 anni in su, con <span className="font-semibold text-[#E9D9AE]">prima lezione gratuita</span>.
            </p>
            <p className="mt-3 max-w-xl text-sm text-[#F7F1E6]/65">
              Un percorso musicale curato, progressivo e accogliente, guidato dalla Dott.ssa/Maestro Licia Luana Morelli.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <PrimaryCTA onClick={goForm} testid="mes-hero-cta-primary">Prenota la lezione gratuita</PrimaryCTA>
              <button onClick={goOpenDay} data-testid="mes-hero-cta-secondary" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#F7F1E6]/30 px-7 py-3.5 text-sm font-semibold uppercase tracking-wider text-[#F7F1E6] transition-all duration-300 hover:border-[#E9D9AE] hover:text-[#E9D9AE]">
                <CalendarDays size={16} /> Ricevi la data dell’Open Day
              </button>
            </div>
            <div className="mt-9 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#F7F1E6]/70">
              {['Piccoli gruppi, grande attenzione', 'Metodo americano Faber', 'Prima lezione gratuita', 'Sede a Legnano'].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><Check size={13} className="text-[#C9A24B]" /> {t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Percorsi */}
      <section id="percorsi" className="mx-auto max-w-6xl px-5 py-24">
        <Reveal className="mb-14 text-center">
          <Kicker>Per chi è</Kicker>
          <h2 className="font-serif text-3xl font-bold sm:text-4xl" style={{ fontFamily: 'Georgia, serif' }}>Due percorsi, un inizio musicale su misura</h2>
        </Reveal>
        <div className="grid gap-8 md:grid-cols-2">
          {[
            { img: IMG.group, icon: Users, tag: '4 – 6 anni · piccolo gruppo', title: 'Propedeutica del pianoforte', pts: ['Gruppi da 2 a 4 bambini', 'Durata 45 minuti', 'Ritmo, ascolto, gioco musicale e partecipazione attiva', 'Primo approccio graduale, musicale e sereno'], price: '15 € a lezione, per bambino', interest: 'group_4_6' },
            { img: IMG.individual, icon: User, tag: 'dai 6 anni · individuale', title: 'Lezioni individuali di pianoforte', pts: ['Lezione uno-a-uno', 'Durata 50 minuti', 'Percorso personalizzato sul bambino', 'Serio ma mai rigido'], price: '25 € a lezione', interest: 'individual_6_plus' },
          ].map((c, i) => (
            <Reveal key={c.title} delay={i * 120}>
              <div className="group h-full overflow-hidden rounded-3xl bg-white shadow-[0_20px_60px_-30px_rgba(20,33,61,0.35)] ring-1 ring-[#14213D]/5 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_70px_-30px_rgba(20,33,61,0.45)]">
                <div className="h-56 overflow-hidden">
                  <img src={c.img} alt={c.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-8">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0E4C4C]/8 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#0E4C4C]"><c.icon size={13} /> {c.tag}</span>
                  <h3 className="mt-4 font-serif text-2xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>{c.title}</h3>
                  <ul className="mt-4 space-y-2.5">
                    {c.pts.map((p) => (
                      <li key={p} className="flex items-start gap-2.5 text-sm text-[#14213D]/75"><Check size={16} className="mt-0.5 shrink-0 text-[#C9A24B]" /> {p}</li>
                    ))}
                  </ul>
                  <div className="mt-6 flex items-center justify-between border-t border-[#14213D]/8 pt-5">
                    <span className="text-lg font-bold text-[#14746F]">{c.price}</span>
                    <button onClick={goForm} className="inline-flex items-center gap-1 text-sm font-semibold text-[#C9A24B] transition-colors hover:text-[#14213D]">Prova gratuita <ChevronRight size={15} /></button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Perché MES — Licia */}
      <section className="bg-[#14213D] py-24 text-[#F7F1E6]">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 md:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <div className="relative">
              <img src={IMG.teacher} alt="Maestro Licia Luana Morelli" className="w-full rounded-3xl object-cover shadow-2xl ring-1 ring-[#C9A24B]/30" />
              <div className="absolute -bottom-4 -right-4 rounded-2xl bg-[#C9A24B] px-5 py-3 text-[#14213D] shadow-xl">
                <p className="text-2xl font-bold leading-none">30+</p>
                <p className="text-[11px] font-semibold uppercase tracking-wider">anni di didattica</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <Kicker>Perché MES</Kicker>
            <h2 className="font-serif text-3xl font-bold sm:text-4xl" style={{ fontFamily: 'Georgia, serif' }}>Una guida esperta, un’attenzione internazionale</h2>
            <p className="mt-5 text-[#F7F1E6]/80 leading-relaxed">
              I corsi sono guidati dalla <span className="font-semibold text-[#E9D9AE]">Dott.ssa/Maestro Licia Luana Morelli</span>, docente con esperienza didattica
              oltre trentennale tra Stati Uniti ed Europa, laureata in Flute Performance / Flauto Traverso.
            </p>
            <p className="mt-4 text-[#F7F1E6]/80 leading-relaxed">
              Un’autorevolezza internazionale unita a sensibilità educativa: la musica insegnata con metodo, cura e rispetto dei tempi di ogni bambino.
            </p>
            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              {[
                { icon: Star, t: 'Esperienza USA ed Europa' },
                { icon: Music2, t: 'Laurea in Flauto Traverso' },
                { icon: MapPin, t: 'Sede all’interno di Syrinx' },
                { icon: Sparkles, t: 'Ambiente educativo accogliente' },
              ].map((b) => (
                <div key={b.t} className="flex items-center gap-3 rounded-xl border border-[#F7F1E6]/10 bg-[#F7F1E6]/5 px-4 py-3">
                  <b.icon size={18} className="text-[#C9A24B]" /> <span className="text-sm">{b.t}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Metodo */}
      <section id="metodo" className="mx-auto max-w-6xl px-5 py-24">
        <div className="grid items-center gap-12 md:grid-cols-2">
          <Reveal>
            <img src={IMG.keys} alt="Dettaglio tastiera pianoforte" className="w-full rounded-3xl object-cover shadow-[0_20px_60px_-30px_rgba(20,33,61,0.4)]" />
          </Reveal>
          <Reveal delay={120}>
            <Kicker>Il metodo</Kicker>
            <h2 className="font-serif text-3xl font-bold sm:text-4xl" style={{ fontFamily: 'Georgia, serif' }}>Metodo americano e propedeutica musicale intelligente</h2>
            <div className="mt-6 space-y-5">
              {[
                { t: 'Metodo americano Faber', d: 'Lo studio del pianoforte con uno dei metodi più apprezzati al mondo: progressivo, chiaro e musicale fin dalle prime note.' },
                { t: 'Propedeutica pratica e coinvolgente', d: 'Ritmo, ascolto, gioco musicale, corpo e partecipazione attiva: la musica si vive, non si subisce.' },
                { t: 'Approccio graduale, serio ma non rigido', d: 'Per i più piccoli un inizio sereno; dai 6 anni un lavoro individuale che personalizza il percorso.' },
              ].map((m) => (
                <div key={m.t} className="flex gap-4">
                  <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0E4C4C]/10 text-[#0E4C4C]"><Check size={18} /></div>
                  <div><h3 className="font-semibold text-[#14213D]">{m.t}</h3><p className="mt-1 text-sm leading-relaxed text-[#14213D]/70">{m.d}</p></div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Come si svolgono */}
      <section className="bg-[#0E4C4C] py-20 text-[#F7F1E6]">
        <div className="mx-auto max-w-6xl px-5">
          <Reveal className="mb-12 text-center">
            <h2 className="font-serif text-3xl font-bold sm:text-4xl" style={{ fontFamily: 'Georgia, serif' }}>Come si svolgono le lezioni</h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-3">
            {[
              { icon: Users, t: 'Piccoli gruppi', d: 'Gruppi da 2 a 4 bambini per i 4-6 anni: attenzione reale a ciascuno.' },
              { icon: User, t: 'Lezioni individuali', d: 'Uno-a-uno dai 6 anni, con percorso su misura.' },
              { icon: Clock, t: 'Orari e organizzazione', d: 'Dal lunedì al giovedì, fascia 16:00 – 19:00.' },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 100}>
                <div className="h-full rounded-2xl border border-[#F7F1E6]/12 bg-[#F7F1E6]/5 p-7 text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#C9A24B]/20 text-[#E9D9AE]"><c.icon size={22} /></div>
                  <h3 className="font-semibold">{c.t}</h3>
                  <p className="mt-2 text-sm text-[#F7F1E6]/75">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* L'aula a Legnano */}
      <section id="aula" className="mx-auto max-w-6xl px-5 py-24">
        <Reveal className="mb-14 text-center">
          <Kicker>La nostra aula</Kicker>
          <h2 className="font-serif text-3xl font-bold sm:text-4xl" style={{ fontFamily: 'Georgia, serif' }}>L’aula di pianoforte a Legnano</h2>
          <p className="mx-auto mt-4 max-w-2xl text-[#14213D]/70">
            Uno spazio reale, curato e accogliente all’interno della Scuola Associazione Syrinx: quattro postazioni con pianoforte digitale, luce calda e un ambiente pensato per i bambini.
          </p>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2">
          {[
            { img: IMG.aula1, cap: 'Quattro postazioni per le lezioni di gruppo' },
            { img: IMG.aula2, cap: 'Ambiente sereno, strumenti Montessori e spazio morbido' },
          ].map((a, i) => (
            <Reveal key={a.cap} delay={i * 120}>
              <figure className="group overflow-hidden rounded-3xl bg-white shadow-[0_20px_60px_-30px_rgba(20,33,61,0.4)] ring-1 ring-[#14213D]/5">
                <div className="aspect-[3/2] overflow-hidden">
                  <img src={a.img} alt={a.cap} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <figcaption className="flex items-center gap-2.5 px-6 py-4 text-sm text-[#14213D]/75">
                  <MapPin size={16} className="shrink-0 text-[#C9A24B]" /> {a.cap}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Prezzi */}
      <section id="prezzi" className="mx-auto max-w-6xl px-5 py-24">
        <Reveal className="mb-14 text-center">
          <Kicker>Prezzi e prima lezione gratuita</Kicker>
          <h2 className="font-serif text-3xl font-bold sm:text-4xl" style={{ fontFamily: 'Georgia, serif' }}>Un primo passo semplice e chiaro</h2>
        </Reveal>
        <div className="grid gap-8 md:grid-cols-2">
          {[
            { tag: 'Gruppo 4-6 anni', price: '15 €', unit: 'a lezione, per bambino', pts: ['Gruppi da 2 a 4 bambini', 'Durata 45 minuti', 'Propedeutica musicale'], interest: 'group_4_6' },
            { tag: 'Individuale dai 6 anni', price: '25 €', unit: 'a lezione', pts: ['Lezione individuale', 'Durata 50 minuti', 'Percorso personalizzato'], featured: true, interest: 'individual_6_plus' },
          ].map((p, i) => (
            <Reveal key={p.tag} delay={i * 120}>
              <div className={`relative h-full rounded-3xl p-8 ${p.featured ? 'bg-[#14213D] text-[#F7F1E6] ring-2 ring-[#C9A24B]' : 'bg-white text-[#14213D] ring-1 ring-[#14213D]/8'} shadow-[0_20px_60px_-30px_rgba(20,33,61,0.35)]`}>
                {p.featured && <span className="absolute -top-3 left-8 rounded-full bg-[#C9A24B] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#14213D]">Più scelto</span>}
                <p className={`text-[11px] font-semibold uppercase tracking-[0.2em] ${p.featured ? 'text-[#E9D9AE]' : 'text-[#14746F]'}`}>{p.tag}</p>
                <div className="mt-3 flex items-end gap-2">
                  <span className="font-serif text-5xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>{p.price}</span>
                  <span className={`mb-1.5 text-sm ${p.featured ? 'text-[#F7F1E6]/70' : 'text-[#14213D]/60'}`}>{p.unit}</span>
                </div>
                <ul className="mt-6 space-y-2.5">
                  {p.pts.map((x) => (<li key={x} className="flex items-center gap-2.5 text-sm"><Check size={16} className="text-[#C9A24B]" /> {x}</li>))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <div className="flex flex-col items-center gap-4 rounded-3xl bg-gradient-to-r from-[#C9A24B]/15 to-[#14746F]/10 px-8 py-8 text-center ring-1 ring-[#C9A24B]/30 sm:flex-row sm:justify-between sm:text-left">
            <div>
              <p className="font-serif text-xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>La prima lezione è gratuita</p>
              <p className="mt-1 text-sm text-[#14213D]/70">Corsi da lunedì a giovedì · 16:00 – 19:00 · nessun impegno</p>
            </div>
            <PrimaryCTA onClick={goForm} testid="mes-prezzi-cta">Prenota la lezione gratuita</PrimaryCTA>
          </div>
        </Reveal>
      </section>

      {/* Open Day */}
      <section id="open-day" className="relative overflow-hidden bg-[#14213D] py-24 text-[#F7F1E6]">
        <img src={IMG.group} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="relative mx-auto max-w-5xl px-5">
          <Reveal className="text-center">
            <Kicker>Open Day</Kicker>
            <h2 className="font-serif text-3xl font-bold sm:text-4xl" style={{ fontFamily: 'Georgia, serif' }}>Vieni all’Open Day MES</h2>
            <p className="mx-auto mt-4 max-w-2xl text-[#F7F1E6]/80">
              Una mattina di sabato per conoscerci con calma: un’esperienza accogliente, rassicurante e ben organizzata, senza alcuna pressione.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Users, t: 'Laboratorio di gruppo', d: 'Per bambini 4-6 anni: ritmo, ascolto e gioco musicale.' },
              { icon: User, t: 'Mini prove individuali', d: 'Per i bambini dai 6 anni, conoscitive e serene.' },
              { icon: Sparkles, t: 'Momento con i genitori', d: 'Un breve confronto finale per rispondere a ogni domanda.' },
              { icon: Check, t: 'Prenoti la prova', d: 'Possibilità di fissare subito la lezione gratuita.' },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 90}>
                <div className="h-full rounded-2xl border border-[#F7F1E6]/12 bg-[#F7F1E6]/5 p-6">
                  <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-[#C9A24B]/20 text-[#E9D9AE]"><c.icon size={20} /></div>
                  <h3 className="font-semibold">{c.t}</h3>
                  <p className="mt-1.5 text-sm text-[#F7F1E6]/70">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-11 text-center">
            <SecondaryCTA onClick={goForm} testid="mes-openday-cta" className="!border-[#E9D9AE]/40 !bg-transparent !text-[#E9D9AE] hover:!bg-[#C9A24B] hover:!text-[#14213D]">Ricevi la data dell’Open Day</SecondaryCTA>
          </div>
        </div>
      </section>

      {/* Fiducia */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {[
              { icon: Users, t: 'Piccoli gruppi' },
              { icon: MapPin, t: 'Sede fisica a Legnano' },
              { icon: Star, t: 'Guida esperta' },
              { icon: Check, t: 'Prima lezione gratuita' },
              { icon: Sparkles, t: 'Promossa da Syrinx' },
            ].map((b) => (
              <div key={b.t} className="flex items-center gap-3 rounded-2xl bg-white px-5 py-4 shadow-[0_10px_30px_-20px_rgba(20,33,61,0.4)] ring-1 ring-[#14213D]/5">
                <b.icon size={18} className="shrink-0 text-[#14746F]" /> <span className="text-sm font-medium">{b.t}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-3xl px-5 py-20">
        <Reveal className="mb-10 text-center">
          <Kicker>Domande frequenti</Kicker>
          <h2 className="font-serif text-3xl font-bold sm:text-4xl" style={{ fontFamily: 'Georgia, serif' }}>Tutto quello che ti serve sapere</h2>
        </Reveal>
        <div className="space-y-3">
          {FAQ_ITEMS.map((f, i) => (
            <div key={i} className="overflow-hidden rounded-2xl bg-white ring-1 ring-[#14213D]/6">
              <button
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                data-testid={`mes-faq-${i}`}
                className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
              >
                <span className="font-semibold text-[#14213D]">{f.q}</span>
                <ChevronDown size={20} className={`shrink-0 text-[#14746F] transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} />
              </button>
              <div className="grid transition-all duration-300" style={{ gridTemplateRows: openFaq === i ? '1fr' : '0fr' }}>
                <div className="overflow-hidden">
                  <p className="px-6 pb-5 text-sm leading-relaxed text-[#14213D]/70">{f.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contatti + form */}
      <section id="contatti" className="bg-[#14213D] py-24 text-[#F7F1E6]">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 lg:grid-cols-2">
          <Reveal>
            <Kicker>Contatti</Kicker>
            <h2 className="font-serif text-3xl font-bold sm:text-4xl" style={{ fontFamily: 'Georgia, serif' }}>Prenota la tua lezione gratuita</h2>
            <p className="mt-4 max-w-md text-[#F7F1E6]/75">
              A Legnano, un nuovo modo di iniziare il pianoforte. Lasciaci i tuoi dati o contattaci direttamente: ti rispondiamo con calma e attenzione.
            </p>
            <div className="mt-8 space-y-4 text-sm">
              <a href={PHONE} className="flex items-center gap-3 text-[#F7F1E6]/85 transition-colors hover:text-[#E9D9AE]"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F7F1E6]/8"><Phone size={17} /></span> +39 344 734 1134</a>
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" data-testid="mes-whatsapp" className="flex items-center gap-3 text-[#F7F1E6]/85 transition-colors hover:text-[#E9D9AE]"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F7F1E6]/8"><MessageCircle size={17} /></span> WhatsApp · +39 344 734 1134</a>
              <a href={`mailto:${EMAIL}`} className="flex items-center gap-3 text-[#F7F1E6]/85 transition-colors hover:text-[#E9D9AE]"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F7F1E6]/8"><Mail size={17} /></span> {EMAIL}</a>
              <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-[#F7F1E6]/85 transition-colors hover:text-[#E9D9AE]"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F7F1E6]/8"><Instagram size={17} /></span> @mesmusicandenglish</a>
              <div className="flex items-start gap-3 text-[#F7F1E6]/85"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F7F1E6]/8"><MapPin size={17} /></span> <span>Corso Sempione, 117 — angolo Via Savonarola<br />20025 Legnano (MI) · presso Scuola Associazione Syrinx</span></div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="rounded-3xl bg-[#F7F1E6] p-8 text-[#14213D] shadow-2xl">
              {status === 'done' ? (
                <div className="flex flex-col items-center py-10 text-center" data-testid="mes-form-success">
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#14746F] text-white"><Check size={28} /></div>
                  <h3 className="font-serif text-2xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>Grazie, richiesta ricevuta</h3>
                  <p className="mt-2 max-w-xs text-sm text-[#14213D]/70">Ti ricontattiamo con le prime disponibilità per la lezione gratuita o con le informazioni sull’Open Day.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="mes-lead-form">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Nome e cognome del genitore *" name="parent_name" value={form.parent_name} onChange={onChange} required />
                    <Field label="Nome del bambino" name="child_name" value={form.child_name} onChange={onChange} />
                    <Field label="Età del bambino" name="child_age" value={form.child_age} onChange={onChange} placeholder="es. 5" />
                    <Field label="Telefono" name="phone" value={form.phone} onChange={onChange} type="tel" />
                  </div>
                  <Field label="Email *" name="email" value={form.email} onChange={onChange} type="email" required />
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#14213D]/60">Interesse</label>
                    <select name="interest" value={form.interest} onChange={onChange} data-testid="mes-form-interest" className="w-full rounded-xl border border-[#14213D]/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[#14746F]">
                      <option value="">Seleziona…</option>
                      <option value="group_4_6">Gruppo 4-6 anni</option>
                      <option value="individual_6_plus">Individuale dai 6 anni</option>
                      <option value="open_day">Open Day</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#14213D]/60">Messaggio</label>
                    <textarea name="message" value={form.message} onChange={onChange} rows={3} className="w-full resize-none rounded-xl border border-[#14213D]/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[#14746F]" />
                  </div>
                  {status === 'error' && <p className="text-sm font-medium text-red-600" data-testid="mes-form-error">Qualcosa è andato storto. Riprova o scrivici via email.</p>}
                  <button type="submit" disabled={status === 'sending'} data-testid="mes-form-submit" className="group flex w-full items-center justify-center gap-2 rounded-full bg-[#C9A24B] px-7 py-4 text-sm font-semibold uppercase tracking-wider text-[#14213D] transition-all duration-300 hover:bg-[#14213D] hover:text-[#E9D9AE] disabled:opacity-60">
                    {status === 'sending' ? 'Invio…' : 'Prenota la lezione gratuita'} <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
                  </button>
                  <p className="text-center text-xs text-[#14213D]/55">Lasciaci i tuoi dati: ti ricontatteremo con le prime disponibilità per la lezione gratuita o con le informazioni sull’Open Day.</p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0E1830] py-10 text-center text-[#F7F1E6]/55">
        <img src={IMG.logo} alt="MES" className="mx-auto mb-3 h-11 w-11 rounded-full object-cover ring-1 ring-[#C9A24B]/40" />
        <p className="text-sm font-semibold text-[#F7F1E6]/80">MES Music and English School</p>
        <p className="mt-1 text-xs">Scuola Associazione Syrinx · Corso Sempione 117, Legnano (MI)</p>
        <p className="mt-3 text-xs">© 2026 MES Music and English School</p>
      </footer>

      {/* Sticky mobile CTA */}
      <div className="fixed inset-x-0 bottom-0 z-40 flex gap-2 border-t border-[#14213D]/10 bg-[#F7F1E6]/95 p-3 backdrop-blur-md lg:hidden">
        <button onClick={goForm} data-testid="mes-sticky-cta" className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#C9A24B] px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#14213D]">
          <Sparkles size={15} /> Lezione gratuita
        </button>
        <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center rounded-full bg-[#0E4C4C] px-5 py-3 text-[#F7F1E6]"><MessageCircle size={18} /></a>
      </div>

      <style>{`
        @keyframes mesKenburns { 0% { transform: scale(1) translateY(0); } 100% { transform: scale(1.12) translateY(-1.5%); } }
        .mes-kenburns { animation: mesKenburns 22s ease-in-out infinite alternate; }
        @media (prefers-reduced-motion: reduce) { .mes-kenburns { animation: none; } }
      `}</style>
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = 'text', required = false, placeholder = '' }) => (
  <div>
    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#14213D]/60">{label}</label>
    <input
      type={type} name={name} value={value} onChange={onChange} required={required} placeholder={placeholder}
      data-testid={`mes-form-${name}`}
      className="w-full rounded-xl border border-[#14213D]/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[#14746F]"
    />
  </div>
);

export default MesPianoLanding;
